import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import BarChartIcon from "@mui/icons-material/BarChart";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PercentIcon from "@mui/icons-material/Percent";

function StudentResultHistoryCheck() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(undefined);
  const [studentLoading, setStudentLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searched, setSearched] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [downloading, setDownloading] = useState(false);

  const user = useMemo(() => {
    try {
      const storedUser = sessionStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (err) {
      return null;
    }
  }, []);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  function getMonthName(month) {
    const months = [
      "",
      "JANUARY",
      "FEBRUARY",
      "MARCH",
      "APRIL",
      "MAY",
      "JUNE",
      "JULY",
      "AUGUST",
      "SEPTEMBER",
      "OCTOBER",
      "NOVEMBER",
      "DECEMBER",
    ];
    return months[month] || month;
  }

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getBranchName = (targetStudent = student) =>
    targetStudent?.branch_name ||
    targetStudent?.branch?.branch_name ||
    results.find((row) => row.branch_name)?.branch_name ||
    "";

  const getStudentLabel = () =>
    `${student?.student_name || "-"} | Roll No: ${student?.student_rollno || "-"} | Branch: ${
      getBranchName() || "-"
    }`;

  const enrichStudentWithBranchName = async (targetStudent) => {
    if (!targetStudent) return targetStudent;
    if (
      getBranchName(targetStudent) ||
      !targetStudent.branch_id ||
      !user?.client_id ||
      !user?.college_id
    ) {
      return targetStudent;
    }

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/branches/${user.client_id}/${user.college_id}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const branches = res.data?.data || res.data?.items || res.data || [];
      const branch = Array.isArray(branches)
        ? branches.find((item) => String(item.branch_id) === String(targetStudent.branch_id))
        : null;
      return { ...targetStudent, branch_name: branch?.branch_name || "" };
    } catch (err) {
      return targetStudent;
    }
  };

  const formatMonthYear = (month, year) => {
    if (!month) return "";
    const monthText = String(getMonthName(month));
    const yearText = year ? String(year) : "";
    return yearText && !monthText.includes(yearText) ? `${monthText}-${yearText}` : monthText;
  };

  const formatNumber = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric.toFixed(0);
  };

  const formatDecimal = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric.toFixed(2);
  };

  const gradeToPoint = (grade) => {
    const normalized = String(grade || "")
      .trim()
      .toUpperCase();
    const points = {
      O: 10,
      "A+": 9,
      A: 8,
      "B+": 7,
      B: 6,
      C: 5,
      D: 4,
      F: 0,
      AB: 0,
    };
    return points[normalized] ?? null;
  };

  const getEffectiveGradePoint = (row) => {
    const storedPoint = Number(row.grade_point ?? row.grade_points);
    const gradePoint = gradeToPoint(row.grade);

    if (!Number.isNaN(storedPoint) && storedPoint > 0) return storedPoint;
    if (gradePoint !== null) return gradePoint;
    return Number.isNaN(storedPoint) ? 0 : storedPoint;
  };

  const getEffectiveAverage = (storedValue, calculatedValue) => {
    const storedNumber = Number(storedValue);
    if (!Number.isNaN(storedNumber) && storedNumber > 0) return storedNumber;
    return calculatedValue;
  };

  const calculateGradeAverage = useCallback((items) => {
    const totals = items.reduce(
      (acc, row) => {
        const credits = Number(row.credits || 0);
        const gradePoint = getEffectiveGradePoint(row);
        if (!Number.isNaN(credits) && !Number.isNaN(gradePoint) && credits > 0) {
          acc.credits += credits;
          acc.gradePoints += credits * gradePoint;
        }
        return acc;
      },
      { credits: 0, gradePoints: 0 }
    );

    return totals.credits ? totals.gradePoints / totals.credits : null;
  }, []);

  const isFailStatus = (row) => {
    const status = String(row?.status || "").toUpperCase();
    const grade = String(row?.grade || "").toUpperCase();
    return (
      status.includes("FAIL") ||
      status.includes("ABSENT") ||
      status === "AB" ||
      grade === "F" ||
      grade === "AB"
    );
  };

  const findStudentBySearchText = async (term) => {
    try {
      const rollUrl = `${API_BASE_URL}/api/students/rollno/${user.client_id}/${
        user.college_id
      }/${encodeURIComponent(term)}`;
      const res = await axios.get(rollUrl, { headers: getAuthHeaders() });
      return res.data?.student || res.data?.data || null;
    } catch (err) {
      const listUrl = `${API_BASE_URL}/api/students/list/${user.client_id}/${user.college_id}`;
      const res = await axios.get(listUrl, { headers: getAuthHeaders() });
      const students = Array.isArray(res.data?.data) ? res.data.data : [];
      const normalized = term.toLowerCase();

      return (
        students.find((item) => String(item.student_rollno || "").toLowerCase() === normalized) ||
        students.find((item) =>
          String(item.student_name || "")
            .toLowerCase()
            .includes(normalized)
        ) ||
        null
      );
    }
  };

  const fetchResults = async (targetStudent) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        student_id: targetStudent.student_id,
        client_id: user.client_id,
        college_id: user.college_id || "",
      });
      const url = `${API_BASE_URL}/api/studentresulthistory/student-final-results?${params.toString()}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setResults(res.data?.data || []);
    } catch (err) {
      setResults([]);
      showSnackbar("Error fetching result history", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchStudent = async () => {
    const term = searchText.trim();
    if (!term) {
      showSnackbar("Please enter roll number or student name", "warning");
      return;
    }
    if (!user?.client_id || !user?.college_id) {
      showSnackbar("Client or college details not found", "error");
      return;
    }

    setSearched(true);
    setStudentLoading(true);
    setStudent(undefined);
    setResults([]);

    try {
      const foundStudent = await findStudentBySearchText(term);
      if (foundStudent?.student_id) {
        const studentWithBranch = await enrichStudentWithBranchName(foundStudent);
        setStudent(studentWithBranch);
        await fetchResults(studentWithBranch);
      } else {
        setStudent(null);
        showSnackbar("Student Details are not found", "warning");
      }
    } catch (err) {
      setStudent(null);
      showSnackbar("Error searching student", "error");
    } finally {
      setStudentLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!student || !results.length) {
      showSnackbar("No result data to download", "warning");
      return;
    }

    setDownloading(true);
    const params = new URLSearchParams({
      student_id: student.student_id,
      client_id: user.client_id,
    });

    window.open(
      `${API_BASE_URL}/api/studentresulthistory/download-result-history-pdf?${params}`,
      "_blank"
    );
    setDownloading(false);
  };

  const currentRowsForSummary = useMemo(() => {
    const latestRowsByCourse = new Map();
    results.forEach((row) => {
      const key = row.result_id;
      if (!latestRowsByCourse.has(key)) {
        latestRowsByCourse.set(key, row);
      }
    });
    return Array.from(latestRowsByCourse.values());
  }, [results]);

  const summaryRow = useMemo(
    () =>
      currentRowsForSummary.find((row) => row.cgpa !== undefined && row.cgpa !== null) ||
      currentRowsForSummary.find(
        (row) => row.percentage !== undefined && row.percentage !== null
      ) ||
      currentRowsForSummary.find((row) => row.sgpa !== undefined && row.sgpa !== null) ||
      currentRowsForSummary[0] ||
      {},
    [currentRowsForSummary]
  );

  const calculatedCgpa = useMemo(
    () => calculateGradeAverage(currentRowsForSummary),
    [calculateGradeAverage, currentRowsForSummary]
  );
  const overallCgpa = formatDecimal(
    getEffectiveAverage(summaryRow.cgpa ?? summaryRow.overall_cgpa, calculatedCgpa)
  );
  const overallPercentage =
    summaryRow.percentage !== undefined && summaryRow.percentage !== null
      ? `${formatDecimal(summaryRow.percentage)}%`
      : "-";

  const rows = useMemo(
    () =>
      results.map((row, index) => ({
        id: row.result_id || `${row.course_code || "course"}-${index}`,
        study_year: row.year_number || "",
        semester: row.semester_number || "",
        exam_type: row.exam_type_name || "",
        month_year: formatMonthYear(row.exam_month || row.passed_month, row.passed_year),
        course_code: row.course_code || "",
        course_name: row.course_name || row.subject_name || "",
        attempts: row.attempts || row.attempt_no || 1,
        internal: formatNumber(row.internal_marks),
        external: formatNumber(row.external_marks),
        total: formatNumber(row.total_marks),
        credits: formatNumber(row.credits),
        grade: row.grade || "",
        grade_points: formatNumber(getEffectiveGradePoint(row)),
        grade_point_value: getEffectiveGradePoint(row),
        credit_value: row.credits,
        sgpa: row.sgpa ?? null,
        status: row.status || "",
      })),
    [results]
  );

  const groupedByYearSemester = useMemo(() => {
    const groups = new Map();
    rows.forEach((row) => {
      const key = `${row.study_year || ""}-${row.semester || ""}`;
      if (!groups.has(key)) {
        groups.set(key, {
          year: row.study_year || "",
          semester: row.semester || "",
          sgpa: getEffectiveAverage(row.sgpa, null),
          data: [],
        });
      }
      const rowSgpa = getEffectiveAverage(row.sgpa, null);
      if (groups.get(key).sgpa === null && rowSgpa !== null) {
        groups.get(key).sgpa = rowSgpa;
      }
      groups.get(key).data.push(row);
    });
    return Array.from(groups.values())
      .sort(
        (a, b) =>
          Number(a.year || 0) - Number(b.year || 0) ||
          Number(a.semester || 0) - Number(b.semester || 0)
      )
      .map((group) => ({
        ...group,
        sgpa: getEffectiveAverage(
          group.sgpa,
          calculateGradeAverage(
            group.data.map((row) => ({
              credits: row.credit_value,
              grade_point: row.grade_point_value,
            }))
          )
        ),
      }));
  }, [calculateGradeAverage, rows]);

  const columns = [
    {
      field: "id",
      headerName: "S.No",
      width: 60,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "course_code", headerName: "Course Code", width: 95 },
    { field: "course_name", headerName: "Course Name", flex: 1, minWidth: 180 },
    { field: "exam_type", headerName: "ExamType", width: 85 },
    { field: "month_year", headerName: "MonthYear", width: 115 },
    {
      field: "attempts",
      headerName: "Attempts",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "internal",
      headerName: "Internal",
      width: 70,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "external",
      headerName: "External",
      width: 75,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "total",
      headerName: "Total",
      width: 65,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "credits",
      headerName: "Credits",
      width: 65,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 65,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "grade_points",
      headerName: "Grade Points",
      width: 85,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      width: 85,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value || "-"}
          size="medium"
          sx={{
            color: isFailStatus(params.row) ? "#d32f2f" : "#000",
            fontWeight: 800,
            borderRadius: 1,
            backgroundColor: isFailStatus(params.row) ? "#ffebee" : "#f2f4f7",
          }}
        />
      ),
    },
  ];

  return (
    <Box p={2} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={1}
        sx={{
          width: "100%",

          margin: "0 auto",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            STUDENT RESULT HISTORY
          </Typography>
          {student && (
            <Chip
              label={getStudentLabel()}
              sx={{
                color: "#14883e",
                backgroundColor: "#e7f6ec",
                fontWeight: 900,
                borderRadius: 1,
                fontSize: 14,
                height: 34,
              }}
            />
          )}
        </Toolbar>
        <Box p={2} sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack spacing={2}>
            <Grid container alignItems="center" sx={{ gap: 1, flexWrap: "wrap" }}>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="medium"
                  label="Roll Number or Student Name"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleSearchStudent();
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: "16px",
                    },
                  }}
                  inputProps={{
                    style: {
                      fontSize: "17px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSearchStudent}
                  disabled={studentLoading}
                  sx={{ color: "#fff", "&:hover": { color: "#fff" }, minHeight: 42 }}
                >
                  {studentLoading ? "Searching..." : "Search"}
                </Button>
              </Grid>
            </Grid>

            <Grid container alignItems="center" sx={{ gap: 1, flexWrap: "wrap" }}>
              <Grid item xs={12} md={2.5}>
                <Box
                  sx={{
                    color: "#14883e",
                    backgroundColor: "#e7f6ec",
                    borderRadius: 1,
                    minHeight: 56,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    border: "1px solid #bfe6ca",
                  }}
                >
                  <EmojiEventsIcon sx={{ fontSize: 26 }} />
                  <Box>
                    <Typography variant="caption" sx={{ display: "block", fontWeight: 800 }}>
                      Overall CGPA
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1 }}>
                      {overallCgpa}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.5}>
                <Box
                  sx={{
                    color: "#0f5fb8",
                    backgroundColor: "#e8f1fd",
                    borderRadius: 1,
                    minHeight: 56,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    border: "1px solid #bcd7fb",
                  }}
                >
                  <PercentIcon sx={{ fontSize: 26 }} />
                  <Box>
                    <Typography variant="caption" sx={{ display: "block", fontWeight: 800 }}>
                      Percentage
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1 }}>
                      {overallPercentage}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={!student || !results.length || downloading}
                  sx={{
                    bgcolor: "#dc3545",
                    "&:hover": { bgcolor: "#c82333" },
                    color: "#fff",
                    minHeight: 56,
                    whiteSpace: "nowrap",
                    fontWeight: 900,
                    px: 2,
                  }}
                >
                  Download Your Result PDF
                </Button>
              </Grid>
            </Grid>

            {studentLoading ? (
              <Typography variant="h6">Loading student info...</Typography>
            ) : student === null ? (
              <Typography variant="h6" color="error">
                Student Details are not found.
              </Typography>
            ) : !searched ? (
              <Typography variant="body2" color="text.secondary">
                Enter a roll number or student name and click Search.
              </Typography>
            ) : (
              <>
                {groupedByYearSemester.map((group) => (
                  <Box key={`${group.year}-${group.semester}`} sx={{ mt: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1f2a3d",
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <BarChartIcon sx={{ fontSize: 24 }} />
                      {group.year} Year, {group.semester} Semester Result History
                    </Typography>
                    <DataGrid
                      rows={group.data}
                      columns={columns}
                      autoHeight
                      loading={loading}
                      disableRowSelectionOnClick
                      hideFooter
                      sx={{
                        width: "100%",
                        border: "1px solid #dce3ec",
                        borderRadius: 1,
                        background: "#fff",
                        "& .MuiDataGrid-cell": {
                          fontSize: 12,
                          px: 0.75,
                        },
                        "& .MuiDataGrid-columnHeader": {
                          px: 0.75,
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#eef3f8",
                          color: "#1f2a3d",
                          fontWeight: 800,
                          fontSize: 12,
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: 800,
                        },
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ mt: 1, color: "#1f2a3d", fontWeight: 800, textAlign: "right" }}
                    >
                      SGPA: {formatDecimal(group.sgpa)}
                    </Typography>
                  </Box>
                ))}
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  <b>Note:</b> In case of any deviation, the concern students may contact the
                  COE(Examination Cell) immediately.
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default StudentResultHistoryCheck;
